import type { Dispatch, SetStateAction } from "react";
import { Link } from "react-router-dom";

/**
 * 系統資訊頁——⚙️ 設定組兩個交互 TAB（模塊化跟進 2026-09-20：自 SystemInfoPage.tsx 拆出，
 * 原文逐字搬遷）：🏷️ 品牌設定＋🛡️ 對話上限。state 留父層，經 props 傳遞。
 */

export function SettingsTabs({
  tab,
  brandValue, setBrandValue, brandLoading, brandSaving, brandMsg, saveBrand,
  abuseForm, setAbuseForm, abuseLoading, abuseSaving, abuseMsg, saveAbuse,
  trialEnabled, trialReport, trialLoading, trialSaving, trialMsg, saveTrialEnabled,
}: {
  tab: "brand" | "abuse" | "trial" | string;
  brandValue: string;
  setBrandValue: Dispatch<SetStateAction<string>>;
  brandLoading: boolean;
  brandSaving: boolean;
  brandMsg: { ok: boolean; text: string } | null;
  saveBrand: () => void;
  abuseForm: { lite: number; pro: number; resetMin: number; burstLite: number; burstPro: number; burstWindowMin: number; burstEnabled: boolean };
  setAbuseForm: Dispatch<SetStateAction<{ lite: number; pro: number; resetMin: number; burstLite: number; burstPro: number; burstWindowMin: number; burstEnabled: boolean }>>;
  abuseLoading: boolean;
  abuseSaving: boolean;
  abuseMsg: { ok: boolean; text: string } | null;
  saveAbuse: () => void;
  trialEnabled: boolean;
  trialReport: Record<string, unknown> | null;
  trialLoading: boolean;
  trialSaving: boolean;
  trialMsg: { ok: boolean; text: string } | null;
  saveTrialEnabled: (next: boolean) => void;
}) {
  if (tab !== "brand" && tab !== "abuse" && tab !== "trial") return null;
  return (
    <>
          {/* ═══════════ TAB：🏷️ 品牌設定 ═══════════ */}
          {tab === "brand" && (
            <div className="space-y-6 max-w-3xl">
              <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
                <h2 className="text-lg font-semibold text-emerald-400 mb-2">
                  🏷️ 平台預設署名
                </h2>
                <p className="text-xs text-slate-400 mb-4 leading-relaxed">
                  每則 AI 回覆最底一行（blockquote 斜體格式自動加上）＋轉真人通知按鈕的 footer。
                  <span className="text-amber-400">呢個係「租戶開咗署名但冇填自訂內容」時用嘅預設</span>
                  ——租戶冇開署名就完全唔顯示。改完約 1 分鐘內生效（對話服務 60 秒快取）。清空＝啟用署名嘅租戶都唔顯示。
                </p>
                {brandLoading ? (
                  <p className="text-sm text-slate-500">載入中…</p>
                ) : (
                  <>
                    <div className="flex gap-3 items-start">
                      <input
                        type="text"
                        value={brandValue}
                        onChange={(e) => setBrandValue(e.target.value)}
                        placeholder="© AIBot"
                        maxLength={120}
                        className="flex-1 px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500 text-sm font-mono"
                      />
                      <button
                        type="button"
                        onClick={saveBrand}
                        disabled={brandSaving}
                        className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
                      >
                        {brandSaving ? "儲存中…" : "💾 儲存"}
                      </button>
                    </div>
                    <div className="mt-4 p-3 bg-slate-950 rounded-lg border border-slate-700/50">
                      <p className="text-xs text-slate-500 mb-1">啟用署名＋未自訂嘅租戶，客人見到：</p>
                      <p className="text-sm text-slate-200">
                        ……回覆內容最後一段
                        {brandValue.trim() ? (
                          <span className="block mt-2 text-slate-400 italic border-l-2 border-slate-600 pl-2">
                            _{brandValue.trim()}_
                          </span>
                        ) : (
                          <span className="block mt-2 text-slate-500">（無署名）</span>
                        )}
                      </p>
                    </div>
                    {brandMsg && (
                      <p className={`mt-3 text-sm ${brandMsg.ok ? "text-emerald-400" : "text-rose-400"}`}>
                        {brandMsg.text}
                      </p>
                    )}
                  </>
                )}
              </div>

            </div>
          )}

          {/* ═══════════ TAB：🛡️ 對話上限（非誠勿擾）═══════════ */}
          {tab === "abuse" && (
            <div className="space-y-6 max-w-3xl">
              <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
                <h2 className="text-lg font-semibold text-emerald-400 mb-2">
                  🛡️ 同一客人 30 分鐘內對話上限（非誠勿擾）
                </h2>
                <p className="text-xs text-slate-400 mb-4 leading-relaxed">
                  每個對話 session 內，同一客人嘅 AI 回覆達上限後，AI 會答埋最後一題然後邀請轉真人（<span className="text-amber-400">唔會硬斷對話</span>）。
                  閒置超過設定分鐘數，計數自動歸零。平台統一規則，按方案套用（Lite 用 Lite 上限；Pro／legacy 用 Pro 上限）。
                </p>
                {abuseLoading ? (
                  <p className="text-sm text-slate-500">載入中…</p>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Lite 上限（次，預設 10）</label>
                        <input
                          type="number"
                          min={1}
                          value={abuseForm.lite}
                          onChange={(e) => setAbuseForm((p) => ({ ...p, lite: Number(e.target.value) }))}
                          className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:border-emerald-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Pro / legacy 上限（次，預設 15）</label>
                        <input
                          type="number"
                          min={1}
                          value={abuseForm.pro}
                          onChange={(e) => setAbuseForm((p) => ({ ...p, pro: Number(e.target.value) }))}
                          className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:border-emerald-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">閒置重置（分鐘，預設 30）</label>
                        <input
                          type="number"
                          min={1}
                          value={abuseForm.resetMin}
                          onChange={(e) => setAbuseForm((p) => ({ ...p, resetMin: Number(e.target.value) }))}
                          className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:border-emerald-500 text-sm"
                        />
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-slate-800">
                      <h3 className="text-sm font-semibold text-rose-300 mb-2">🛡️ 跨號碼 burst limit（封堵換號碼繞過）</h3>
                      <p className="text-xs text-slate-400 mb-3 leading-relaxed">
                        同一租戶、任何號碼，喺指定窗口內累積 AI 回覆達上限即 soft-stop（發固定話術＋轉真人），唔會無限燒 token。
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <label className="block text-xs text-slate-400 mb-1">Lite burst（次，預設 20）</label>
                          <input
                            type="number"
                            min={1}
                            value={abuseForm.burstLite}
                            onChange={(e) => setAbuseForm((p) => ({ ...p, burstLite: Number(e.target.value) }))}
                            className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:border-emerald-500 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-400 mb-1">Pro burst（次，預設 50）</label>
                          <input
                            type="number"
                            min={1}
                            value={abuseForm.burstPro}
                            onChange={(e) => setAbuseForm((p) => ({ ...p, burstPro: Number(e.target.value) }))}
                            className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:border-emerald-500 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-400 mb-1">窗口（分鐘，預設 15）</label>
                          <input
                            type="number"
                            min={1}
                            value={abuseForm.burstWindowMin}
                            onChange={(e) => setAbuseForm((p) => ({ ...p, burstWindowMin: Number(e.target.value) }))}
                            className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:border-emerald-500 text-sm"
                          />
                        </div>
                        <div className="flex items-end">
                          <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={abuseForm.burstEnabled}
                              onChange={(e) => setAbuseForm((p) => ({ ...p, burstEnabled: e.target.checked }))}
                              className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-emerald-500 focus:ring-emerald-500"
                            />
                            啟用 burst limit
                          </label>
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={saveAbuse}
                      disabled={abuseSaving}
                      className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      {abuseSaving ? "儲存中…" : "💾 儲存"}
                    </button>
                    {abuseMsg && (
                      <p className={`mt-3 text-sm ${abuseMsg.ok ? "text-emerald-400" : "text-rose-400"}`}>
                        {abuseMsg.text}
                      </p>
                    )}
                  </>
                )}
              </div>
              <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
                <p className="text-xs text-slate-400">
                  想睇每個租戶實際生效值？去側欄「<Link to="/anti-abuse" className="text-emerald-400 hover:underline">🛡️ 反濫用上限</Link>」頁（同一設定，另有逐租戶一覽表）。
                </p>
              </div>
            </div>
          )}

          {/* ═══════════ TAB：🧪 試用漏斗（線 2 平台開關＋即時數）═══════════ */}
          {tab === "trial" && (
            <div className="space-y-6 max-w-3xl">
              <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
                <h2 className="text-lg font-semibold text-emerald-400 mb-2">
                  🧪 試用漏斗平台開關
                </h2>
                <p className="text-xs text-slate-400 mb-4 leading-relaxed">
                  線 2 試用轉化（100 則／72 小時／30 並行池）。<span className="text-amber-400">關閉＝新領取即時擋下</span>
                  （客人撳領取掣會收到「試用功能暫停」訊息；定時任務——到期提醒／斷線／再互動——同步停跑）；
                  <span className="text-amber-400">已試用中的名額不受影響</span>（照跑至到期；排隊輪替／付款轉化／報表照常運作）。即時生效。
                </p>
                {trialLoading ? (
                  <p className="text-sm text-slate-500">載入中…</p>
                ) : (
                  <>
                    <div className="flex items-center gap-4">
                      <span className={`text-sm font-semibold ${trialEnabled ? "text-emerald-400" : "text-rose-400"}`}>
                        {trialEnabled ? "🟢 開啟中" : "🔴 已關閉"}
                      </span>
                      {trialEnabled ? (
                        <button
                          type="button"
                          onClick={() => saveTrialEnabled(false)}
                          disabled={trialSaving}
                          className="px-5 py-2 bg-rose-600 hover:bg-rose-500 disabled:bg-slate-700 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          {trialSaving ? "處理中…" : "⏸️ 關閉試用漏斗"}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => saveTrialEnabled(true)}
                          disabled={trialSaving}
                          className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          {trialSaving ? "處理中…" : "▶️ 開啟試用漏斗"}
                        </button>
                      )}
                    </div>
                    {trialMsg && (
                      <p className={`mt-3 text-sm ${trialMsg.ok ? "text-emerald-400" : "text-rose-400"}`}>
                        {trialMsg.text}
                      </p>
                    )}
                    {trialReport && (
                      <div className="mt-5 pt-4 border-t border-slate-800">
                        <h3 className="text-sm font-semibold text-slate-200 mb-2">📊 近 30 日即時數</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {Object.entries(trialReport)
                            .filter(([, v]) => typeof v === "number")
                            .map(([k, v]) => (
                              <div key={k} className="bg-slate-950 rounded-lg border border-slate-700/50 p-3">
                                <p className="text-[10px] text-slate-500 font-mono break-all">{k}</p>
                                <p className="text-xl font-bold text-slate-100">{String(v)}</p>
                              </div>
                            ))}
                          {Object.values(trialReport).every((v) => typeof v !== "number") && (
                            <p className="text-xs text-slate-500 col-span-4">（暫無數據）</p>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
                <p className="text-xs text-slate-400">
                  詳細漏斗報表（事件級 E1-E15＋時距）可呼叫 <code className="text-emerald-400">GET /admin/trial/report</code>；管理操作（排隊領取／轉化確認）：<code className="text-emerald-400">/admin/trial/offer-claim</code>、<code className="text-emerald-400">/admin/trial/convert</code>。
                </p>
              </div>
            </div>
          )}

    </>
  );
}
